import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem as SelectMenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Box,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';
import { PolicyTypes } from '@flashbacktech/flashbackclient/api';
import { toastSuccess, toastError } from '../utils/toast';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { usePolicyPermissions } from '../hooks/usePolicyPermissions';

// Action type constants (matching PolicyActionType enum values)
const ACTION_TYPE = {
  LOG: 0,
  ALERT: 1,
  BLOCK: 2,
} as const;

interface AIPoliciesProps {
  embedded?: boolean;
  // Navigation context props (alternative to URL params)
  initialWorkspaceId?: string;
  initialRepoId?: string;
}

const AIPolicies: React.FC<AIPoliciesProps> = ({ 
  embedded = false,
  initialWorkspaceId,
  initialRepoId,
}) => {
  const { globalData } = useAuth();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();
  const permissions = usePolicyPermissions();
  const navigate = useNavigate();
  
  // Get scope from URL params or props
  const [searchParams] = useSearchParams();
  const scopeContext = useMemo(() => {
    // Props take priority over URL params
    const workspaceId = initialWorkspaceId || searchParams.get('workspaceId') || undefined;
    const repoId = initialRepoId || searchParams.get('repoId') || undefined;
    
    // Determine scope level
    let level: 'org' | 'workspace' | 'repo' = 'org';
    if (repoId && workspaceId) {
      level = 'repo';
    } else if (workspaceId) {
      level = 'workspace';
    }
    
    return { level, workspaceId, repoId };
  }, [initialWorkspaceId, initialRepoId, searchParams]);
  
  // Get workspace/repo names for display
  const scopeDisplayInfo = useMemo(() => {
    const workspace = scopeContext.workspaceId 
      ? globalData.workspaces.find(w => w.id === scopeContext.workspaceId)
      : null;
    return {
      workspaceName: workspace?.name || scopeContext.workspaceId || '',
      repoName: scopeContext.repoId || '', // TODO: fetch repo name if needed
    };
  }, [scopeContext, globalData.workspaces]);
  
  // Check if user has access to this scope
  const hasAccessToScope = useMemo(() => {
    if (scopeContext.level === 'org') {
      return permissions.canManageOrgPolicies;
    }
    if (scopeContext.workspaceId) {
      return permissions.canManageWorkspacePolicies(scopeContext.workspaceId);
    }
    return false;
  }, [scopeContext, permissions]);
  
  // Scope selector state
  const [scopeSelector, setScopeSelector] = useState<'all' | 'organization' | 'workspace' | 'repositories'>('all');
  const [selectedScopeWorkspaceId, setSelectedScopeWorkspaceId] = useState<string | null>(null);
  const [selectedScopeRepoId, setSelectedScopeRepoId] = useState<string | null>(null);
  const [scopeRepos, setScopeRepos] = useState<Array<{id: string; name: string}>>([]);
  const [scopeReposLoading, setScopeReposLoading] = useState(false);

  const [activeTab, setActiveTab] = useState<'policies' | 'violations' | 'alerts'>('policies');
  const [policies, setPolicies] = useState<PolicyTypes.PolicyDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Violations state
  const [violations, setViolations] = useState<PolicyTypes.PolicyViolationDTO[]>([]);
  const [violationsLoading, setViolationsLoading] = useState(false);
  const [violationsTotal, setViolationsTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const itemsPerPage = 20;

  // Alerts state
  const [alerts, setAlerts] = useState<PolicyTypes.PolicyAlertDTO[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsTotal, setAlertsTotal] = useState(0);
  const [alertsPage, setAlertsPage] = useState(1);
  const [alertsDateFrom, setAlertsDateFrom] = useState<string>('');
  const [alertsDateTo, setAlertsDateTo] = useState<string>('');
  
  // Filters
  const [filterRisk, setFilterRisk] = useState<PolicyTypes.RiskType | 'ALL'>('ALL');
  const [filterAction, setFilterAction] = useState<number | 'ALL'>('ALL');

  // Edit Modal (kept for edit only)
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<PolicyTypes.PolicyDTO | null>(null);
  const [modalData, setModalData] = useState<Partial<PolicyTypes.CreatePolicyRequest>>({
    name: '',
    content: '',
    riskType: PolicyTypes.RiskType.LOW,
    actionType: ACTION_TYPE.LOG,
    orgId: '',
    workspaceId: null,
    repoId: null,
  });

  // Create form scope selection (organization, workspace, or repository)
  const [createScopeType, setCreateScopeType] = useState<'organization' | 'workspace' | 'repository'>('organization');
  const [createScopeWorkspaceId, setCreateScopeWorkspaceId] = useState<string>('');
  const [createScopeRepoId, setCreateScopeRepoId] = useState<string>('');
  const [createScopeRepos, setCreateScopeRepos] = useState<Array<{id: string; name: string}>>([]);
  const [createScopeReposLoading, setCreateScopeReposLoading] = useState(false);

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState<PolicyTypes.PolicyDTO | null>(null);

  // Menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPolicyForMenu, setSelectedPolicyForMenu] = useState<PolicyTypes.PolicyDTO | null>(null);

  // Repos for display (when at repo level)
  const [repoInfo, setRepoInfo] = useState<{ id: string; name: string } | null>(null);

  // Load repos when scope selector picks a workspace for 'repositories' mode
  useEffect(() => {
    if (scopeSelector === 'repositories' && selectedScopeWorkspaceId) {
      const loadRepos = async () => {
        setScopeReposLoading(true);
        try {
          const response = (await withTokenRefresh(() => apiClient.getStorageRepos(selectedScopeWorkspaceId))) as any;
          if (response.success && response.repos) {
            setScopeRepos(response.repos.map((r: any) => ({ id: r.id, name: r.name })));
          }
        } catch (error) {
          console.error('[AIPolicies] Error loading repos for scope:', error);
        } finally {
          setScopeReposLoading(false);
        }
      };
      loadRepos();
    }
  }, [scopeSelector, selectedScopeWorkspaceId, withTokenRefresh, apiClient]);

  // Load repo info when at repo level
  useEffect(() => {
    if (scopeContext.level === 'repo' && scopeContext.repoId && scopeContext.workspaceId) {
      loadRepoInfo(scopeContext.workspaceId, scopeContext.repoId);
    }
  }, [scopeContext]);
  
  const loadRepoInfo = async (workspaceId: string, repoId: string) => {
    try {
      const response = (await withTokenRefresh(() => apiClient.getStorageRepos(workspaceId))) as any;
      if (response.success && response.repos) {
        const repo = response.repos.find((r: any) => r.id === repoId);
        if (repo) {
          setRepoInfo({ id: repo.id, name: repo.name });
        }
      }
    } catch (error) {
      console.error('[AIPolicies] Error loading repo info:', error);
    }
  };

  // Load all data on mount
  useEffect(() => {
    loadPolicies();
    loadViolations();
    loadAlerts();
  }, []);

  // Reload violations when filters change or tab is active
  useEffect(() => {
    if (activeTab === 'violations') {
      loadViolations();
    }
  }, [activeTab, currentPage, dateFrom, dateTo]);

  // Reload alerts when filters change or tab is active
  useEffect(() => {
    if (activeTab === 'alerts') {
      loadAlerts();
    }
  }, [activeTab, alertsPage, alertsDateFrom, alertsDateTo]);

  const loadPolicies = async () => {
    if (!globalData?.organization?.id) return;
    
    setLoading(true);
    try {
      const orgId = globalData.organization.id;
      
      // Fetch org-level policies
      const orgResponse = await withTokenRefresh(() => apiClient.getPolicies({ orgId } as PolicyTypes.GetPoliciesQuery));
      const allPolicies: PolicyTypes.PolicyDTO[] = orgResponse.success ? (orgResponse.policies || []) : [];
      
      // Also fetch workspace-level policies for each workspace
      const workspaces = globalData.workspaces || [];
      if (workspaces.length > 0) {
        const wsPromises = workspaces.map((ws: any) =>
          withTokenRefresh(() => apiClient.getPolicies({ orgId, workspaceId: ws.id } as PolicyTypes.GetPoliciesQuery))
            .catch(() => ({ success: false, policies: [] }))
        );
        const wsResponses = await Promise.all(wsPromises);
        for (const wsResp of wsResponses) {
          if (wsResp.success && wsResp.policies) {
            // Only add policies not already in the list (avoid duplicates)
            for (const p of wsResp.policies) {
              if (!allPolicies.find((existing: any) => existing.id === p.id)) {
                allPolicies.push(p);
              }
            }
          }
        }
      }
      
      console.log('[AIPolicies] Total policies loaded:', allPolicies.length, allPolicies.map((p: any) => ({ id: p.id, name: p.name, workspaceId: p.workspaceId })));
      setPolicies(allPolicies);
    } catch (error: any) {
      console.error('[AIPolicies] Error loading policies:', error);
      toastError(error.message || 'Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const loadViolations = async () => {
    setViolationsLoading(true);
    try {
      const params: PolicyTypes.GetPolicyViolationsQuery = {
        skip: (currentPage - 1) * itemsPerPage,
        take: itemsPerPage,
      };
      
      if (dateFrom) {
        params.from = new Date(dateFrom).toISOString();
      }
      
      if (dateTo) {
        params.to = new Date(dateTo).toISOString();
      }
      
      const response = await withTokenRefresh(() => apiClient.getPolicyViolations(params));
      console.log('[AIPolicies] loadViolations params:', params);
      console.log('[AIPolicies] loadViolations response:', response);
      
      if (response.success) {
        setViolations(response.violations || []);
        setViolationsTotal(response.total || 0);
      } else {
        const errorMsg = (response as any).message || 'Failed to load violations';
        console.error('[AIPolicies] API returned error:', errorMsg);
        setViolations([]);
        setViolationsTotal(0);
      }
    } catch (error: any) {
      console.error('[AIPolicies] Error loading violations:', error);
      toastError(error.message || 'Failed to load violations');
      setViolations([]);
      setViolationsTotal(0);
    } finally {
      setViolationsLoading(false);
    }
  };

  // Load alerts from API
  const loadAlerts = async () => {
    setAlertsLoading(true);
    try {
      const params: PolicyTypes.GetPolicyAlertsQuery = {
        skip: (alertsPage - 1) * itemsPerPage,
        take: itemsPerPage,
      };
      
      if (alertsDateFrom) {
        params.from = new Date(alertsDateFrom).toISOString();
      }
      
      if (alertsDateTo) {
        params.to = new Date(alertsDateTo).toISOString();
      }
      
      const response = await withTokenRefresh(() => apiClient.getPolicyAlerts(params));
      console.log('[AIPolicies] loadAlerts params:', params);
      console.log('[AIPolicies] loadAlerts response:', response);
      
      if (response.success) {
        setAlerts(response.alerts || []);
        setAlertsTotal(response.total || 0);
      } else {
        const errorMsg = (response as any).message || 'Failed to load alerts';
        console.error('[AIPolicies] API returned error:', errorMsg);
        setAlerts([]);
        setAlertsTotal(0);
      }
    } catch (error: any) {
      console.error('[AIPolicies] Error loading alerts:', error);
      toastError(error.message || 'Failed to load alerts');
      setAlerts([]);
      setAlertsTotal(0);
    } finally {
      setAlertsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCreatePolicy = async () => {
    if (!globalData?.organization?.id) return;
    if (!modalData.name || !modalData.content) {
      toastError('Name and content are required');
      return;
    }

    try {
      // Build request with scope from create form dropdown
      const request: PolicyTypes.CreatePolicyRequest = {
        name: modalData.name,
        content: modalData.content,
        riskType: modalData.riskType || PolicyTypes.RiskType.LOW,
        actionType: modalData.actionType !== undefined ? modalData.actionType : ACTION_TYPE.LOG,
        orgId: globalData.organization.id,
        workspaceId: (createScopeType === 'workspace' || createScopeType === 'repository') && createScopeWorkspaceId ? createScopeWorkspaceId : null,
        repoId: createScopeType === 'repository' && createScopeRepoId ? createScopeRepoId : null,
      };

      if (editingPolicy) {
        // Update - only name, content, riskType, actionType can change (not scope)
        await withTokenRefresh(() => apiClient.updatePolicy(editingPolicy.id, {
          name: request.name,
          content: request.content,
          riskType: request.riskType,
          actionType: request.actionType,
        }));
        toastSuccess('Policy updated successfully');
        setOpenEditModal(false);
      } else {
        // Create
        await withTokenRefresh(() => apiClient.createPolicy(request));
        toastSuccess('Policy created successfully');
        setShowCreateForm(false);
      }

      resetModal();
      loadPolicies();
    } catch (error: any) {
      console.error('[AIPolicies] Error creating/updating policy:', error);
      toastError(error.message || 'Failed to save policy');
    }
  };

  const handleEditPolicy = (policy: PolicyTypes.PolicyDTO) => {
    // Check permission before allowing edit
    if (!permissions.canManagePolicy(policy)) {
      toastError('You do not have permission to edit this policy');
      return;
    }
    
    setEditingPolicy(policy);
    setModalData({
      name: policy.name,
      content: policy.content,
      riskType: policy.riskType,
      actionType: policy.actionType,
      orgId: policy.orgId,
      workspaceId: policy.workspaceId,
      repoId: policy.repoId,
    });
    
    setOpenEditModal(true);
    handleMenuClose();
  };

  const handleDeletePolicy = async () => {
    if (!policyToDelete) return;

    try {
      await withTokenRefresh(() => apiClient.deletePolicy(policyToDelete.id));
      toastSuccess('Policy deleted successfully');
      setDeleteDialogOpen(false);
      setPolicyToDelete(null);
      loadPolicies();
    } catch (error: any) {
      console.error('[AIPolicies] Error deleting policy:', error);
      toastError(error.message || 'Failed to delete policy');
    }
  };

  const resetModal = () => {
    setEditingPolicy(null);
    setModalData({
      name: '',
      content: '',
      riskType: PolicyTypes.RiskType.LOW,
      actionType: ACTION_TYPE.LOG,
      orgId: globalData?.organization?.id || '',
      workspaceId: scopeContext.workspaceId || null,
      repoId: scopeContext.repoId || null,
    });
    setCreateScopeType('organization');
    setCreateScopeWorkspaceId('');
    setCreateScopeRepoId('');
    setCreateScopeRepos([]);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, policy: PolicyTypes.PolicyDTO) => {
    setAnchorEl(event.currentTarget);
    setSelectedPolicyForMenu(policy);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPolicyForMenu(null);
  };

  const getRiskColor = (risk: PolicyTypes.RiskType) => {
    switch (risk) {
      case PolicyTypes.RiskType.HIGH:
        return { bg: '#FEF3F2', color: '#F04438', border: '#FECDCA' };
      case PolicyTypes.RiskType.MEDIUM:
        return { bg: '#FFFAEB', color: '#F79009', border: '#FEDF89' };
      case PolicyTypes.RiskType.LOW:
        return { bg: '#ECFDF3', color: '#12B76A', border: '#D1FADF' };
    }
  };

  const getActionLabel = (action: number) => {
    switch (action) {
      case ACTION_TYPE.BLOCK:
        return 'Block';
      case ACTION_TYPE.ALERT:
        return 'Alert';
      case ACTION_TYPE.LOG:
        return 'Log';
      default:
        return 'Unknown';
    }
  };

  const getActionColor = (action: number) => {
    switch (action) {
      case ACTION_TYPE.BLOCK:
        return { bg: '#FEF3F2', color: '#F04438', border: '#FECDCA' };
      case ACTION_TYPE.ALERT:
        return { bg: '#FFFAEB', color: '#F79009', border: '#FEDF89' };
      case ACTION_TYPE.LOG:
        return { bg: '#EFF8FF', color: '#3B82F6', border: '#B2DDFF' };
      default:
        return { bg: '#F3F4F6', color: '#6B7280', border: '#D1D5DB' };
    }
  };

  // Filter policies by scope selector + search + risk + action filters
  const filteredPolicies = policies.filter((policy) => {
    // Scope filter from header selector
    if (scopeSelector === 'organization') {
      if (policy.workspaceId || policy.repoId) return false;
    } else if (scopeSelector === 'workspace') {
      if (!policy.workspaceId) return false;
      if (policy.repoId) return false;
      if (selectedScopeWorkspaceId && policy.workspaceId !== selectedScopeWorkspaceId) return false;
    } else if (scopeSelector === 'repositories') {
      if (!policy.repoId) return false;
      if (selectedScopeWorkspaceId && policy.workspaceId !== selectedScopeWorkspaceId) return false;
      if (selectedScopeRepoId && policy.repoId !== selectedScopeRepoId) return false;
    }
    // 'all' shows everything

    // Search filter
    if (searchQuery && !policy.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !policy.content.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // Risk filter
    if (filterRisk !== 'ALL' && policy.riskType !== filterRisk) {
      return false;
    }
    
    // Action filter
    if (filterAction !== 'ALL' && policy.actionType !== filterAction) {
      return false;
    }
    
    return true;
  });

  // Filter violations by search
  const filteredViolations = violations.filter((violation) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      violation.policyName.toLowerCase().includes(query) ||
      violation.explanation.toLowerCase().includes(query) ||
      violation.userName.toLowerCase().includes(query) ||
      violation.repoName.toLowerCase().includes(query)
    );
  });

  const totalPages = Math.ceil(violationsTotal / itemsPerPage);

  // Get scope title and description
  const getScopeTitle = () => {
    if (scopeContext.level === 'repo') {
      return `Repository Policies: ${repoInfo?.name || scopeContext.repoId}`;
    } else if (scopeContext.level === 'workspace') {
      return `Workspace Policies: ${scopeDisplayInfo.workspaceName}`;
    }
    return 'Organization Policies';
  };
  
  const getScopeDescription = () => {
    if (scopeContext.level === 'repo') {
      return 'Manage governance policies for this repository';
    } else if (scopeContext.level === 'workspace') {
      return 'Manage governance policies for this workspace';
    }
    return 'Manage organization-wide governance policies for AI operations';
  };

  // No access state
  if (!hasAccessToScope) {
    return (
      <div className={embedded ? '' : 'h-full flex flex-col'} style={{ backgroundColor: '#FFFFFF' }}>
        <div className={embedded ? 'px-4' : 'flex-1 px-10 pb-6 overflow-y-auto'}>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Box sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: 64, 
              height: 64, 
              borderRadius: '12px', 
              bgcolor: '#FEF3F2', 
              mb: 2 
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#F04438" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10"/>
                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
              </svg>
            </Box>
            <Typography sx={{ color: '#101828', fontSize: '16px', fontWeight: 600, mb: 0.5 }}>
              No Access
            </Typography>
            <Typography sx={{ color: '#667085', fontSize: '14px' }}>
              You do not have permission to view or manage policies at this level.
            </Typography>
          </Box>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'h-full flex flex-col'} style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header - only show when not embedded */}
      {!embedded && (
        <div style={{ padding: '24px 40px 24px 40px' }}>
          {/* Back button - show when navigated from workspace or repo */}
          {scopeContext.level !== 'org' && (
            <Button
              variant="text"
              startIcon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>}
              onClick={() => {
                if (scopeContext.level === 'repo') {
                  navigate('/repositories/inventory');
                } else if (scopeContext.level === 'workspace') {
                  navigate('/organization?tab=workspace&section=management');
                }
              }}
              sx={{ mb: 2, color: '#667085', textTransform: 'none', fontWeight: 500, '&:hover': { bgcolor: '#F9FAFB' } }}
            >
              {scopeContext.level === 'repo' ? 'Back to Repositories' : 'Back to Workspaces'}
            </Button>
          )}
          <Typography
            style={{
              fontSize: '32px',
              fontWeight: 700,
              color: '#1F2937',
              marginBottom: '8px',
              fontFamily: 'Inter',
              letterSpacing: '-0.02em',
            }}
          >
            {getScopeTitle()}
          </Typography>
          <Typography
            style={{
              fontSize: '16px',
              fontWeight: 400,
              color: '#6B7280',
              fontFamily: 'Inter',
              lineHeight: '24px',
            }}
          >
            {getScopeDescription()}
          </Typography>
        </div>
      )}

      {/* Content */}
      <div className={embedded ? 'px-4' : 'flex-1 px-10 pb-6 overflow-y-auto'}>

      {/* LLM Guardrails Sub-section Header with buttons */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>
              LLM Guardrails
            </Typography>
            <Typography sx={{ fontSize: '14px', color: '#667085' }}>
              {getScopeDescription()}
            </Typography>
          </Box>
          {!showCreateForm && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Scope Selector */}
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={scopeSelector}
                  onChange={(e) => {
                    const val = e.target.value as 'all' | 'organization' | 'workspace' | 'repositories';
                    setScopeSelector(val);
                    setSelectedScopeWorkspaceId(null);
                    setSelectedScopeRepoId(null);
                    setScopeRepos([]);
                  }}
                  sx={{ fontSize: '13px', fontWeight: 500, borderRadius: '8px', height: 36, bgcolor: '#F0F2FF', color: '#617BFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D5DD' } }}
                >
                  <MenuItem value="all" sx={{ fontSize: '13px' }}>All</MenuItem>
                  <MenuItem value="organization" sx={{ fontSize: '13px' }}>Organization</MenuItem>
                  <MenuItem value="workspace" sx={{ fontSize: '13px' }}>Workspace</MenuItem>
                  <MenuItem value="repositories" sx={{ fontSize: '13px' }}>Repositories</MenuItem>
                </Select>
              </FormControl>
              {/* Workspace picker (for workspace or repositories scope) */}
              {(scopeSelector === 'workspace' || scopeSelector === 'repositories') && (
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <Select
                    value={selectedScopeWorkspaceId || ''}
                    onChange={(e) => {
                      setSelectedScopeWorkspaceId(e.target.value || null);
                      setSelectedScopeRepoId(null);
                    }}
                    displayEmpty
                    sx={{ fontSize: '13px', borderRadius: '8px', height: 36, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D5DD' } }}
                  >
                    <MenuItem value="" sx={{ fontSize: '13px', color: '#667085' }}>All workspaces</MenuItem>
                    {globalData.workspaces.map((ws: any) => (
                      <MenuItem key={ws.id} value={ws.id} sx={{ fontSize: '13px' }}>{ws.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {/* Repo picker (for repositories scope, after workspace selected) */}
              {scopeSelector === 'repositories' && selectedScopeWorkspaceId && (
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <Select
                    value={selectedScopeRepoId || ''}
                    onChange={(e) => setSelectedScopeRepoId(e.target.value || null)}
                    displayEmpty
                    sx={{ fontSize: '13px', borderRadius: '8px', height: 36, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D5DD' } }}
                  >
                    <MenuItem value="" sx={{ fontSize: '13px', color: '#667085' }}>{scopeReposLoading ? 'Loading...' : 'All repositories'}</MenuItem>
                    {scopeRepos.map((r) => (
                      <MenuItem key={r.id} value={r.id} sx={{ fontSize: '13px' }}>{r.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {/* Search */}
              {showSearch ? (
                <TextField
                  size="small"
                  placeholder="Search policies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onBlur={() => { if (!searchQuery) setShowSearch(false); }}
                  autoFocus
                  sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '36px' } }}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ fontSize: 18, color: '#667085', mr: 0.5 }} />,
                  }}
                />
              ) : (
                <Tooltip title="Search">
                  <IconButton
                    size="small"
                    onClick={() => setShowSearch(true)}
                    sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
                  >
                    <SearchIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
              {/* Reload */}
              <Tooltip title="Reload">
                <IconButton
                  size="small"
                  onClick={() => loadPolicies()}
                  sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
                >
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              {/* Add - only show if user has permission */}
              {hasAccessToScope && (
                <Tooltip title="Create policy">
                  <IconButton
                    size="small"
                    onClick={() => {
                      resetModal();
                      setShowCreateForm(true);
                    }}
                    sx={{ width: 36, height: 36, bgcolor: '#617BFF', borderRadius: '8px', color: 'white', '&:hover': { bgcolor: '#4A67E3' } }}
                  >
                    <AddIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {!showCreateForm && (
        <>

          {/* Statistics Cards - Requests, Alerts, Violations, Blocks */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
            <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                  </Box>
                  <Typography sx={{ fontSize: '13px', color: '#667085', fontWeight: 500 }}>Requests</Typography>
                </Box>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#101828' }}>{policies.length}</Typography>
                <Typography sx={{ fontSize: '12px', color: '#667085' }}>Active policies</Typography>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                  </Box>
                  <Typography sx={{ fontSize: '13px', color: '#667085', fontWeight: 500 }}>Alerts</Typography>
                </Box>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#F59E0B' }}>{alertsTotal}</Typography>
                <Typography sx={{ fontSize: '12px', color: '#667085' }}>Policy alerts detected</Typography>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: '#FEF3F2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F04438" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                  </Box>
                  <Typography sx={{ fontSize: '13px', color: '#667085', fontWeight: 500 }}>Violations</Typography>
                </Box>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#EF4444' }}>{violationsTotal}</Typography>
                <Typography sx={{ fontSize: '12px', color: '#667085' }}>Policy violations detected</Typography>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                  </Box>
                  <Typography sx={{ fontSize: '13px', color: '#667085', fontWeight: 500 }}>Blocks</Typography>
                </Box>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#DC2626' }}>{policies.filter(p => p.actionType === ACTION_TYPE.BLOCK).length}</Typography>
                <Typography sx={{ fontSize: '12px', color: '#667085' }}>Block policies active</Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Search Bar */}
          {showSearch && (
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                placeholder={activeTab === 'policies' ? 'Search policies...' : 'Search violations...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '& fieldset': { borderColor: '#D0D5DD' },
                    '&:hover fieldset': { borderColor: '#617BFF' },
                  }
                }}
              />
            </Box>
          )}

          {/* Tabs */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, borderBottom: '1px solid #E9EAEB' }}>
            <Box
              onClick={() => setActiveTab('policies')}
              sx={{
                pb: 1.5,
                cursor: 'pointer',
                borderBottom: activeTab === 'policies' ? '2px solid #617BFF' : '2px solid transparent',
                marginBottom: '-1px',
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: activeTab === 'policies' ? '#101828' : '#717680' }}>
                Policies
              </Typography>
            </Box>
            <Box
              onClick={() => setActiveTab('violations')}
              sx={{
                pb: 1.5,
                cursor: 'pointer',
                borderBottom: activeTab === 'violations' ? '2px solid #617BFF' : '2px solid transparent',
                marginBottom: '-1px',
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: activeTab === 'violations' ? '#101828' : '#717680' }}>
                Violations
              </Typography>
            </Box>
            <Box
              onClick={() => setActiveTab('alerts')}
              sx={{
                pb: 1.5,
                cursor: 'pointer',
                borderBottom: activeTab === 'alerts' ? '2px solid #617BFF' : '2px solid transparent',
                marginBottom: '-1px',
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: activeTab === 'alerts' ? '#101828' : '#717680' }}>
                Alerts
              </Typography>
            </Box>
          </Box>
        </>
      )}

      {showCreateForm && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Button
              variant="text"
              startIcon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>}
              onClick={() => { setShowCreateForm(false); resetModal(); }}
              sx={{ mb: 2, color: '#667085', textTransform: 'none', fontWeight: 500, '&:hover': { bgcolor: '#F9FAFB' } }}
            >
              Back to Policies
            </Button>
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>
            Create AI Policy
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#667085', mb: 3 }}>
            Define governance rules for AI operations in your organization.
          </Typography>
        </>
      )}

      {activeTab === 'violations' && !showCreateForm ? (
        /* Violations Tab */
        <>
          {/* Filters */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            {/* Date From */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ fontSize: '14px' }}
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ fontSize: '14px' }}
              />
            </div>

            {/* Clear Filters */}
            {(dateFrom || dateTo) && (
              <button
                onClick={() => {
                  setDateFrom('');
                  setDateTo('');
                  setCurrentPage(1);
                }}
                className="px-3 py-2 text-sm text-blue-600 hover:text-blue-800"
              >
                Clear filters
              </button>
            )}
          </div>

          {/* Violations List */}
          {violationsLoading ? (
            <div className="text-center py-12">
              <Typography style={{ color: '#667085' }}>Loading violations...</Typography>
            </div>
          ) : filteredViolations.length === 0 ? (
            <div className="text-center py-12">
              <div 
                className="inline-block p-6 rounded-lg border"
                style={{ 
                  backgroundColor: '#F9FAFB',
                  borderColor: '#E5E7EB',
                }}
              >
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9CA3AF"
                  strokeWidth="1.5"
                  className="mx-auto mb-4"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <Typography style={{ color: '#667085', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
                  {searchQuery || dateFrom || dateTo
                    ? 'No violations found'
                    : 'No policy violations detected'}
                </Typography>
                <Typography style={{ color: '#9CA3AF', fontSize: '13px' }}>
                  {searchQuery || dateFrom || dateTo
                    ? 'Try adjusting your filters to see more results'
                    : 'All AI operations are compliant with your policies'}
                </Typography>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {filteredViolations.map((violation) => (
                  <div
                    key={violation.id}
                    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {/* Warning Badge */}
                          <Chip
                            label="VIOLATION"
                            size="small"
                            style={{
                              backgroundColor: '#FEF3F2',
                              color: '#F04438',
                              border: '1px solid #FECDCA',
                              fontWeight: 600,
                              fontSize: '12px',
                              height: '22px',
                            }}
                          />

                          {/* Timestamp */}
                          <Typography
                            style={{
                              fontSize: '12px',
                              color: '#667085',
                            }}
                          >
                            {formatDate(violation.timestamp instanceof Date ? violation.timestamp.toISOString() : violation.timestamp)}
                          </Typography>
                        </div>

                        <Typography
                          style={{
                            fontWeight: 600,
                            fontSize: '16px',
                            color: '#101828',
                            marginBottom: '4px',
                          }}
                        >
                          {violation.policyName}
                        </Typography>

                        <Typography
                          style={{
                            fontSize: '14px',
                            color: '#667085',
                            lineHeight: '20px',
                            marginBottom: '8px',
                          }}
                        >
                          {violation.explanation}
                        </Typography>

                        <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                          <span>👤 {violation.userName}</span>
                          <span>📁 {violation.repoName}</span>
                          {violation.conversationId && (
                            <span>💬 {violation.conversationId.substring(0, 8)}...</span>
                          )}
                          <span>🔑 {violation.repoAiApiKeyName}</span>
                          {violation.llmType && violation.llmModel && (
                            <span>🤖 {violation.llmType} / {violation.llmModel}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <Typography style={{ fontSize: '14px', color: '#667085' }}>
                    Showing {(currentPage - 1) * itemsPerPage + 1} -{' '}
                    {Math.min(currentPage * itemsPerPage, violationsTotal)} of {violationsTotal} violations
                  </Typography>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      style={{ fontSize: '14px' }}
                    >
                      Previous
                    </button>
                    <span style={{ fontSize: '14px', color: '#667085' }}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      style={{ fontSize: '14px' }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : activeTab === 'alerts' && !showCreateForm ? (
        /* Alerts Tab */
        <>
          {/* Filters */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            {/* Date From */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">From</label>
              <input
                type="date"
                value={alertsDateFrom}
                onChange={(e) => setAlertsDateFrom(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ fontSize: '14px' }}
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">To</label>
              <input
                type="date"
                value={alertsDateTo}
                onChange={(e) => setAlertsDateTo(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ fontSize: '14px' }}
              />
            </div>

            {/* Clear Filters */}
            {(alertsDateFrom || alertsDateTo) && (
              <button
                onClick={() => {
                  setAlertsDateFrom('');
                  setAlertsDateTo('');
                  setAlertsPage(1);
                }}
                className="px-3 py-2 text-sm text-blue-600 hover:text-blue-800"
              >
                Clear filters
              </button>
            )}
          </div>

          {/* Alerts List */}
          {alertsLoading ? (
            <div className="text-center py-12">
              <Typography style={{ color: '#667085' }}>Loading alerts...</Typography>
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-12">
              <div 
                className="inline-block p-6 rounded-lg border"
                style={{ 
                  backgroundColor: '#F9FAFB',
                  borderColor: '#E5E7EB',
                }}
              >
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9CA3AF"
                  strokeWidth="1.5"
                  className="mx-auto mb-4"
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                <Typography style={{ color: '#667085', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
                  {alertsDateFrom || alertsDateTo
                    ? 'No alerts found'
                    : 'No policy alerts'}
                </Typography>
                <Typography style={{ color: '#9CA3AF', fontSize: '13px' }}>
                  {alertsDateFrom || alertsDateTo
                    ? 'Try adjusting your filters to see more results'
                    : 'No alerts have been triggered by your policies'}
                </Typography>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {alerts.map((alert) => (
                  <div
                    key={alert.id}
                    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {/* Alert Badge */}
                          <Chip
                            label="ALERT"
                            size="small"
                            style={{
                              backgroundColor: '#FFFAEB',
                              color: '#F79009',
                              border: '1px solid #FEDF89',
                              fontWeight: 600,
                              fontSize: '12px',
                              height: '22px',
                            }}
                          />

                          {/* Timestamp */}
                          <Typography
                            style={{
                              fontSize: '12px',
                              color: '#667085',
                            }}
                          >
                            {formatDate(alert.timestamp instanceof Date ? alert.timestamp.toISOString() : alert.timestamp)}
                          </Typography>
                        </div>

                        <Typography
                          style={{
                            fontWeight: 600,
                            fontSize: '16px',
                            color: '#101828',
                            marginBottom: '4px',
                          }}
                        >
                          {alert.policyName}
                        </Typography>

                        <Typography
                          style={{
                            fontSize: '14px',
                            color: '#667085',
                            lineHeight: '20px',
                            marginBottom: '8px',
                          }}
                        >
                          {alert.message}
                        </Typography>

                        <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                          <span>👤 {alert.userName}</span>
                          <span>📁 {alert.repoName}</span>
                          {alert.conversationId && (
                            <span>💬 {alert.conversationId.substring(0, 8)}...</span>
                          )}
                          <span>🔑 {alert.repoAiApiKeyName}</span>
                          {alert.llmType && alert.llmModel && (
                            <span>🤖 {alert.llmType} / {alert.llmModel}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {Math.ceil(alertsTotal / itemsPerPage) > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <Typography style={{ fontSize: '14px', color: '#667085' }}>
                    Showing {(alertsPage - 1) * itemsPerPage + 1} -{' '}
                    {Math.min(alertsPage * itemsPerPage, alertsTotal)} of {alertsTotal} alerts
                  </Typography>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setAlertsPage((p) => Math.max(1, p - 1))}
                      disabled={alertsPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      style={{ fontSize: '14px' }}
                    >
                      Previous
                    </button>
                    <span style={{ fontSize: '14px', color: '#667085' }}>
                      Page {alertsPage} of {Math.ceil(alertsTotal / itemsPerPage)}
                    </span>
                    <button
                      onClick={() => setAlertsPage((p) => Math.min(Math.ceil(alertsTotal / itemsPerPage), p + 1))}
                      disabled={alertsPage === Math.ceil(alertsTotal / itemsPerPage)}
                      className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      style={{ fontSize: '14px' }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : showCreateForm ? (
        /* Create Form */
        <div className="bg-white rounded-lg shadow-sm border border-[#E9EAEB] p-6">
          <Typography 
            variant="h6" 
            className="mb-2"
            style={{
              fontWeight: 600,
              fontSize: '18px',
              color: '#181D27',
            }}
          >
            Create AI Policy
          </Typography>
          <Typography 
            variant="body2" 
            style={{ color: '#535862', marginBottom: '24px' }}
          >
            Define governance rules and enforcement actions for AI operations.
          </Typography>

          <form className="space-y-6">
            {/* Policy Name */}
            <div>
              <label 
                htmlFor="policy-name" 
                className="block text-sm font-medium mb-2"
                style={{ color: '#181D27' }}
              >
                Policy Name <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <input
                id="policy-name"
                type="text"
                value={modalData.name || ''}
                onChange={(e) => setModalData({ ...modalData, name: e.target.value })}
                placeholder="e.g., PII Protection Policy"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ fontSize: '14px' }}
                required
              />
              <p className="mt-1 text-xs" style={{ color: '#6B7280' }}>
                A descriptive name to identify this policy
              </p>
            </div>

            {/* Policy Content */}
            <div>
              <label 
                htmlFor="policy-content" 
                className="block text-sm font-medium mb-2"
                style={{ color: '#181D27' }}
              >
                Policy Content <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <textarea
                id="policy-content"
                value={modalData.content || ''}
                onChange={(e) => setModalData({ ...modalData, content: e.target.value })}
                placeholder="Describe the policy in natural language. Be specific about what is allowed or prohibited.&#10;&#10;Example: Never share personally identifiable information (PII) such as names, email addresses, phone numbers, or social security numbers through AI prompts or responses."
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ fontSize: '14px', resize: 'vertical' }}
                required
              />
              <p className="mt-1 text-xs" style={{ color: '#6B7280' }}>
                Describe the policy rules in natural language with specific examples
              </p>
            </div>

            {/* Risk Type */}
            <div>
              <label 
                htmlFor="risk-type" 
                className="block text-sm font-medium mb-2"
                style={{ color: '#181D27' }}
              >
                Risk Type <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <select
                id="risk-type"
                value={modalData.riskType || PolicyTypes.RiskType.LOW}
                onChange={(e) =>
                  setModalData({ ...modalData, riskType: e.target.value as PolicyTypes.RiskType })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ fontSize: '14px' }}
              >
                <option value={PolicyTypes.RiskType.LOW}>Low - Informational</option>
                <option value={PolicyTypes.RiskType.MEDIUM}>Medium - Important</option>
                <option value={PolicyTypes.RiskType.HIGH}>High - Critical</option>
              </select>
              <p className="mt-1 text-xs" style={{ color: '#6B7280' }}>
                Classify the severity of violations for this policy
              </p>
            </div>

            {/* Action Type */}
            <div>
              <label 
                htmlFor="action-type" 
                className="block text-sm font-medium mb-2"
                style={{ color: '#181D27' }}
              >
                Action Type <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <select
                id="action-type"
                value={modalData.actionType !== undefined ? modalData.actionType.toString() : ACTION_TYPE.LOG.toString()}
                onChange={(e) =>
                  setModalData({ ...modalData, actionType: parseInt(e.target.value) })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ fontSize: '14px' }}
              >
                <option value={ACTION_TYPE.LOG}>Log Only - Record violations</option>
                <option value={ACTION_TYPE.ALERT}>Alert - Log and notify administrators</option>
                <option value={ACTION_TYPE.BLOCK}>Block - Prevent action and alert</option>
              </select>
              <p className="mt-1 text-xs" style={{ color: '#6B7280' }}>
                Define what happens when this policy is violated
              </p>
            </div>

            {/* Policy Scope - Step 1: Organization, Workspace, or Repository */}
            <div>
              <label 
                className="block text-sm font-medium mb-2"
                style={{ color: '#181D27' }}
              >
                Policy Scope <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <select
                value={createScopeType}
                onChange={(e) => {
                  const val = e.target.value as 'organization' | 'workspace' | 'repository';
                  setCreateScopeType(val);
                  if (val === 'organization') {
                    setCreateScopeWorkspaceId('');
                    setCreateScopeRepoId('');
                    setCreateScopeRepos([]);
                  } else if (val === 'workspace') {
                    setCreateScopeRepoId('');
                    setCreateScopeRepos([]);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ fontSize: '14px' }}
              >
                <option value="organization">Organization</option>
                <option value="workspace">Workspace</option>
                <option value="repository">Repository</option>
              </select>
              <p className="mt-1 text-xs" style={{ color: '#6B7280' }}>
                Choose whether this policy applies to the entire organization, a specific workspace, or a repository
              </p>
            </div>

            {/* Policy Scope - Step 2: Workspace selection (if Workspace or Repository is chosen) */}
            {(createScopeType === 'workspace' || createScopeType === 'repository') && (
              <div>
                <label 
                  className="block text-sm font-medium mb-2"
                  style={{ color: '#181D27' }}
                >
                  Select Workspace <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <select
                  value={createScopeWorkspaceId}
                  onChange={(e) => {
                    setCreateScopeWorkspaceId(e.target.value);
                    setCreateScopeRepoId('');
                    if (createScopeType === 'repository' && e.target.value) {
                      // Load repos for selected workspace
                      setCreateScopeReposLoading(true);
                      withTokenRefresh(() => apiClient.getStorageRepos(e.target.value))
                        .then((response: any) => {
                          if (response.success && response.repos) {
                            setCreateScopeRepos(response.repos.map((r: any) => ({ id: r.id, name: r.name })));
                          }
                        })
                        .catch(() => setCreateScopeRepos([]))
                        .finally(() => setCreateScopeReposLoading(false));
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ fontSize: '14px' }}
                >
                  <option value="">-- Select a workspace --</option>
                  {globalData.workspaces.map((ws: any) => (
                    <option key={ws.id} value={ws.id}>
                      {ws.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs" style={{ color: '#6B7280' }}>
                  {createScopeType === 'repository' ? 'Select the workspace containing the repository' : 'The policy will only apply to this workspace'}
                </p>
              </div>
            )}

            {/* Policy Scope - Step 3: Repository selection (only if Repository is chosen) */}
            {createScopeType === 'repository' && createScopeWorkspaceId && (
              <div>
                <label 
                  className="block text-sm font-medium mb-2"
                  style={{ color: '#181D27' }}
                >
                  Select Repository <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <select
                  value={createScopeRepoId}
                  onChange={(e) => setCreateScopeRepoId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ fontSize: '14px' }}
                  disabled={createScopeReposLoading}
                >
                  <option value="">{createScopeReposLoading ? 'Loading repositories...' : '-- Select a repository --'}</option>
                  {createScopeRepos.map((repo) => (
                    <option key={repo.id} value={repo.id}>
                      {repo.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs" style={{ color: '#6B7280' }}>
                  The policy will only apply to this repository
                </p>
              </div>
            )}

            {/* Security Notice with Guide Link */}
            <div 
              className="p-4 rounded-lg border"
              style={{ 
                backgroundColor: '#F0F9FF',
                borderColor: '#BAE6FD',
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#0284C7"
                    strokeWidth="2"
                    style={{ flexShrink: 0, marginTop: '2px' }}
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <div>
                    <Typography 
                      variant="body2" 
                      style={{ 
                        fontWeight: 600,
                        color: '#0C4A6E',
                        marginBottom: '4px',
                      }}
                    >
                      Policy Enforcement
                    </Typography>
                    <Typography 
                      variant="body2" 
                      style={{ 
                        fontSize: '13px',
                        color: '#075985',
                        lineHeight: '18px',
                      }}
                    >
                      Policies are evaluated in real-time during AI operations. All policy operations maintain a complete audit trail for compliance and security.
                    </Typography>
                  </div>
                </div>
                {/* Guide Link */}
                <div
                  onClick={() =>
                    window.open('https://docs.flashback.tech/guides/ai-policies', '_blank')
                  }
                  style={{
                    color: '#4766FF',
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  View guide
                  <span style={{ fontSize: '16px' }}>→</span>
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  resetModal();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg font-medium transition-colors"
                style={{
                  fontSize: '14px',
                  color: '#344054',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#F9FAFB';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreatePolicy}
                className="px-4 py-2 rounded-lg font-medium text-white transition-colors"
                style={{
                  fontSize: '14px',
                  backgroundColor: '#617BFF',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#5A6FE8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#617BFF';
                }}
              >
                Create Policy
              </button>
            </div>
          </form>
        </div>
      ) : (
        /* Policies List */
        <>
          {/* Filters */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            {/* Risk Filter */}
            <FormControl size="small" style={{ minWidth: 120 }}>
          <Select
            value={filterRisk}
            onChange={(e) => setFilterRisk(e.target.value as any)}
            displayEmpty
            style={{ fontSize: '14px' }}
          >
            <SelectMenuItem value="ALL">All Risks</SelectMenuItem>
            <SelectMenuItem value={PolicyTypes.RiskType.HIGH}>High</SelectMenuItem>
            <SelectMenuItem value={PolicyTypes.RiskType.MEDIUM}>Medium</SelectMenuItem>
            <SelectMenuItem value={PolicyTypes.RiskType.LOW}>Low</SelectMenuItem>
          </Select>
        </FormControl>

        {/* Action Filter */}
        <FormControl size="small" style={{ minWidth: 120 }}>
          <Select
            value={filterAction === 'ALL' ? 'ALL' : filterAction.toString()}
            onChange={(e) => setFilterAction(e.target.value === 'ALL' ? 'ALL' : parseInt(e.target.value))}
            displayEmpty
            style={{ fontSize: '14px' }}
          >
            <SelectMenuItem value="ALL">All Actions</SelectMenuItem>
            <SelectMenuItem value={ACTION_TYPE.BLOCK.toString()}>Block</SelectMenuItem>
            <SelectMenuItem value={ACTION_TYPE.ALERT.toString()}>Alert</SelectMenuItem>
            <SelectMenuItem value={ACTION_TYPE.LOG.toString()}>Log</SelectMenuItem>
          </Select>
        </FormControl>

      </div>

      {/* Policies List */}
      {loading ? (
        <div className="text-center py-12">
          <Typography style={{ color: '#667085' }}>Loading policies...</Typography>
        </div>
      ) : filteredPolicies.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Box sx={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            width: 64, 
            height: 64, 
            borderRadius: '12px', 
            bgcolor: '#F9FAFB', 
            mb: 2 
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </Box>
          <Typography sx={{ color: '#101828', fontSize: '14px', fontWeight: 600, mb: 0.5 }}>
            {searchQuery || filterRisk !== 'ALL' || filterAction !== 'ALL'
              ? 'No policies found'
              : 'No policies yet'}
          </Typography>
          <Typography sx={{ color: '#667085', fontSize: '13px' }}>
            {searchQuery || filterRisk !== 'ALL' || filterAction !== 'ALL'
              ? 'Try adjusting your filters to see more results'
              : 'Create your first policy to get started'}
          </Typography>
        </Box>
      ) : (
        /* List View */
        <Box sx={{ border: '1px solid #E9EAEB', borderRadius: '12px', overflow: 'hidden' }}>
          {/* Table Header */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 48px', 
            gap: 2, 
            px: 2, 
            py: 1.5, 
            bgcolor: '#F9FAFB', 
            borderBottom: '1px solid #E9EAEB',
            alignItems: 'center'
          }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#667085', textTransform: 'uppercase' }}>
              Policy
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#667085', textTransform: 'uppercase' }}>
              Scope
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#667085', textTransform: 'uppercase' }}>
              Risk
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#667085', textTransform: 'uppercase' }}>
              Action
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#667085', textTransform: 'uppercase' }}>
              Created
            </Typography>
            <Box />
          </Box>
          
          {/* Table Rows */}
          {filteredPolicies.map((policy, index) => {
            const riskColors = getRiskColor(policy.riskType);
            const actionColors = getActionColor(policy.actionType);
            const canManage = permissions.canManagePolicy(policy);

            return (
              <Box 
                key={policy.id}
                sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 48px', 
                  gap: 2, 
                  px: 2, 
                  py: 1.5, 
                  alignItems: 'center',
                  borderBottom: index < filteredPolicies.length - 1 ? '1px solid #E9EAEB' : 'none',
                  bgcolor: 'white',
                  transition: 'all 0.15s',
                  '&:hover': { 
                    bgcolor: '#F9FAFB'
                  }
                }}
              >
                {/* Policy Name & Content */}
                <Tooltip title={policy.content} placement="top-start">
                  <Box sx={{ overflow: 'hidden' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {policy.name}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#667085', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {policy.content.length > 60 ? `${policy.content.substring(0, 60)}...` : policy.content}
                    </Typography>
                  </Box>
                </Tooltip>

                {/* Scope */}
                <Box>
                  <Chip
                    label={
                      policy.repoId ? 'Repository'
                        : policy.workspaceId ? 'Workspace'
                        : 'Organization'
                    }
                    size="small"
                    sx={{
                      bgcolor: policy.repoId ? '#FEF3C7' : policy.workspaceId ? '#EEF2FF' : '#F3F4F6',
                      color: policy.repoId ? '#92400E' : policy.workspaceId ? '#617BFF' : '#374151',
                      border: `1px solid ${policy.repoId ? '#FDE68A' : policy.workspaceId ? '#C7D2FE' : '#E5E7EB'}`,
                      fontWeight: 500,
                      fontSize: '11px',
                      height: '22px',
                    }}
                  />
                </Box>

                {/* Risk */}
                <Box>
                  <Chip
                    label={policy.riskType}
                    size="small"
                    sx={{
                      bgcolor: riskColors.bg,
                      color: riskColors.color,
                      border: `1px solid ${riskColors.border}`,
                      fontWeight: 600,
                      fontSize: '11px',
                      height: '22px',
                    }}
                  />
                </Box>

                {/* Action */}
                <Box>
                  <Chip
                    label={getActionLabel(policy.actionType)}
                    size="small"
                    sx={{
                      bgcolor: actionColors.bg,
                      color: actionColors.color,
                      border: `1px solid ${actionColors.border}`,
                      fontWeight: 600,
                      fontSize: '11px',
                      height: '22px',
                    }}
                  />
                </Box>

                {/* Created Date */}
                <Typography sx={{ fontSize: '13px', color: '#667085' }}>
                  {new Date(policy.createdAt instanceof Date ? policy.createdAt : policy.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </Typography>

                {/* Actions - only show menu if user can manage this policy */}
                {canManage ? (
                  <IconButton
                    onClick={(e) => handleMenuClick(e, policy)}
                    size="small"
                    sx={{ 
                      color: '#667085',
                      '&:hover': { bgcolor: '#F5F7FF', color: '#617BFF' }
                    }}
                  >
                    <MoreVertIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                ) : (
                  <Box sx={{ width: 28 }} /> // Placeholder for alignment
                )}
              </Box>
            );
          })}
        </Box>
      )}
        </>
      )}

      {/* Recent Policy Events - Below policies list */}
      {!showCreateForm && activeTab === 'policies' && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ 
            bgcolor: 'white', 
            borderRadius: '12px', 
            border: '1px solid #E5E7EB',
            p: 3,
          }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#101828', mb: 3 }}>
              Recent Policy Events
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {violationsLoading || alertsLoading ? (
                <Typography sx={{ fontSize: '13px', color: '#667085', textAlign: 'center', py: 2 }}>
                  Loading policy events...
                </Typography>
              ) : (() => {
                const allEvents = [
                  ...violations.map(v => ({ 
                    type: 'violation' as const, 
                    id: v.id, 
                    policyName: v.policyName, 
                    repoName: v.repoName, 
                    timestamp: v.timestamp,
                    message: v.explanation,
                    userName: v.userName,
                  })), 
                  ...alerts.map(a => ({ 
                    type: 'alert' as const, 
                    id: a.id, 
                    policyName: a.policyName, 
                    repoName: a.repoName, 
                    timestamp: a.timestamp,
                    message: a.message,
                    userName: a.userName,
                  }))
                ]
                  .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                  .slice(0, 10);

                if (allEvents.length === 0) {
                  return (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" style={{ margin: '0 auto 8px' }}>
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                      <Typography sx={{ fontSize: '13px', color: '#667085' }}>
                        No recent policy events
                      </Typography>
                    </Box>
                  );
                }

                return allEvents.map((event) => (
                  <Box key={`${event.type}-${event.id}`} sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: 2,
                    p: 2,
                    bgcolor: '#F9FAFB',
                    borderRadius: '8px',
                  }}>
                    <Box sx={{ 
                      width: 32, 
                      height: 32, 
                      borderRadius: '8px', 
                      bgcolor: event.type === 'violation' ? '#FEF2F2' : '#FFFBEB',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      {event.type === 'violation' ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2">
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#101828', mb: 0.5 }}>
                        {event.type === 'violation' ? 'Violation' : 'Alert'}: {event.policyName}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#667085', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {event.message}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        {event.userName && (
                          <Typography sx={{ fontSize: '10px', color: '#9CA3AF' }}>
                            👤 {event.userName}
                          </Typography>
                        )}
                        {event.repoName && (
                          <Typography sx={{ fontSize: '10px', color: '#9CA3AF' }}>
                            📦 {event.repoName}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: '10px', color: '#9CA3AF', minWidth: 70, textAlign: 'right', flexShrink: 0 }}>
                      {new Date(event.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                ));
              })()}
            </Box>
          </Box>
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedPolicyForMenu && permissions.canManagePolicy(selectedPolicyForMenu) && (
          <>
            <MenuItem
              onClick={() => {
                if (selectedPolicyForMenu) handleEditPolicy(selectedPolicyForMenu);
              }}
            >
              <EditIcon fontSize="small" style={{ marginRight: 8 }} />
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                setPolicyToDelete(selectedPolicyForMenu);
                setDeleteDialogOpen(true);
                handleMenuClose();
              }}
              style={{ color: '#F04438' }}
            >
              <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
              Delete
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Edit Modal */}
      <Dialog
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          resetModal();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Policy</DialogTitle>
        <DialogContent>
          <div className="space-y-4 pt-2">
            {/* Name */}
            <TextField
              fullWidth
              label="Policy Name"
              value={modalData.name || ''}
              onChange={(e) => setModalData({ ...modalData, name: e.target.value })}
              required
            />

            {/* Content */}
            <TextField
              fullWidth
              label="Policy Content"
              multiline
              rows={6}
              value={modalData.content || ''}
              onChange={(e) => setModalData({ ...modalData, content: e.target.value })}
              required
              helperText="Describe the policy in natural language. Be specific about what is allowed or prohibited."
            />

            {/* Risk Type */}
            <FormControl fullWidth>
              <InputLabel>Risk Type</InputLabel>
              <Select
                value={modalData.riskType || PolicyTypes.RiskType.LOW}
                onChange={(e) =>
                  setModalData({ ...modalData, riskType: e.target.value as PolicyTypes.RiskType })
                }
                label="Risk Type"
              >
                <SelectMenuItem value={PolicyTypes.RiskType.LOW}>
                  Low - Informational
                </SelectMenuItem>
                <SelectMenuItem value={PolicyTypes.RiskType.MEDIUM}>
                  Medium - Important
                </SelectMenuItem>
                <SelectMenuItem value={PolicyTypes.RiskType.HIGH}>
                  High - Critical
                </SelectMenuItem>
              </Select>
            </FormControl>

            {/* Action Type */}
            <FormControl fullWidth>
              <InputLabel>Action Type</InputLabel>
              <Select
                value={modalData.actionType !== undefined ? modalData.actionType : ACTION_TYPE.LOG}
                onChange={(e) =>
                  setModalData({ ...modalData, actionType: parseInt(e.target.value as string) })
                }
                label="Action Type"
              >
                <SelectMenuItem value={ACTION_TYPE.LOG}>
                  Log Only - Record violations
                </SelectMenuItem>
                <SelectMenuItem value={ACTION_TYPE.ALERT}>
                  Alert - Log and notify administrators
                </SelectMenuItem>
                <SelectMenuItem value={ACTION_TYPE.BLOCK}>
                  Block - Prevent action and alert
                </SelectMenuItem>
              </Select>
            </FormControl>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditModal(false);
              resetModal();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreatePolicy}
            variant="contained"
            style={{ backgroundColor: '#617BFF' }}
          >
            Update Policy
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Policy</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the policy "{policyToDelete?.name}"? This action cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeletePolicy} style={{ color: '#F04438' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      </div>
    </div>
  );
};

export default AIPolicies;
